import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { 
  LayoutDashboard, Store, BarChart3, MessageSquare, Sparkles, Settings,
  LogOut, ChefHat, GitCompare, Bell, Globe, Users, Moon, Sun, HelpCircle, Shield, FileDown, Building2, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';

const Sidebar = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const { t, lang, toggleLang } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = user?.role === 'superadmin' ? [
    { to: '/superadmin', icon: Shield, label: 'Super Admin' },
    { to: '/superadmin/franchise', icon: Building2, label: t('franchise_nav') || 'Franchising' },
  ] : user?.role === 'master' ? [
    { to: '/master/dashboard', icon: Building2, label: t('master_dashboard_nav') || 'Dashboard Master' },
  ] : [
    { to: '/dashboard', icon: LayoutDashboard, label: t('dashboard') },
    { to: '/restaurants', icon: Store, label: t('restaurants') },
    { to: '/magazzino', icon: Package, label: t('warehouse') || 'Magazzino', badge: 'PRESTO' },
    { to: '/comparison', icon: GitCompare, label: t('comparison') },
    { to: '/reviews', icon: MessageSquare, label: t('reviews') },
    { to: '/ai-insights', icon: Sparkles, label: t('sofia_ai') },
    { to: '/alerts', icon: Bell, label: t('alerts') },
    { to: '/team', icon: Users, label: t('team') || 'Team' },
    { to: '/faq', icon: HelpCircle, label: t('faq') },
  ];

  return (
    <aside className="h-screen w-64 backdrop-blur-xl border-r flex flex-col" style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--background-subtle)' }} data-testid="sidebar">
      <div className="p-6">
        <div className="flex items-center gap-2.5">
          <img src="/images/logo-icon.png" alt="" className="h-10 w-auto" />
          <span className="font-outfit text-lg font-bold" style={{ color: 'var(--text-main)' }}>
            Risto<span style={{ color: '#6BBE44' }}>Metrix</span>
          </span>
        </div>
      </div>

      <Separator className="mx-4" />

      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 ${
                isActive
                  ? 'bg-[#2E4A3D]/10 text-[#2E4A3D] border-r-2 border-[#2E4A3D] font-medium'
                  : 'text-[#78716C] hover:bg-stone-100 hover:text-[#1C1917]'
              }`
            }
            data-testid={`nav-${item.to.slice(1)}`}
          >
            <item.icon className="w-5 h-5" />
            <span className="text-sm flex-1">{item.label}</span>
            {item.badge && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#FFB020] text-[#0E1612] tracking-wider">
                {item.badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <Separator className="mx-4" />

      <div className="p-4 space-y-3">
        {/* Theme & Language */}
        <div className="flex gap-2">
          <button
            onClick={toggleTheme}
            className="flex items-center gap-2 px-3 py-2 rounded-md flex-1 text-left transition-all"
            style={{ color: 'var(--text-muted)' }}
            data-testid="theme-switcher"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            <span className="text-sm">{theme === 'dark' ? 'Light' : 'Dark'}</span>
          </button>
          <button
            onClick={toggleLang}
            className="flex items-center gap-2 px-3 py-2 rounded-md transition-all"
            style={{ color: 'var(--text-muted)' }}
            data-testid="lang-switcher"
          >
            <Globe className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase">{lang}</span>
          </button>
        </div>

        {user?.role !== 'superadmin' && (
          <a
            href={`/api/tutorial/download`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 text-[#78716C] hover:bg-emerald-50 hover:text-emerald-700"
            data-testid="download-tutorial-btn"
          >
            <FileDown className="w-5 h-5" />
            <span className="text-sm">{lang === 'it' ? 'Guida PDF' : lang === 'th' ? 'คู่มือ PDF' : 'PDF Guide'}</span>
          </a>
        )}

        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 ${
              isActive ? 'bg-[#2E4A3D]/10 text-[#2E4A3D]' : 'text-[#78716C] hover:bg-stone-100 hover:text-[#1C1917]'
            }`
          }
          data-testid="nav-settings"
        >
          <Settings className="w-5 h-5" />
          <span className="text-sm">{t('settings')}</span>
        </NavLink>

        <div className="px-4 py-3 rounded-lg" style={{ backgroundColor: 'var(--background-subtle)' }}>
          <p className="text-sm font-medium truncate" style={{ color: 'var(--text-main)' }}>{user?.name}</p>
          <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
        </div>

        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-[#78716C] hover:text-red-600 hover:bg-red-50"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span className="text-sm">{t('logout')}</span>
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
